import React, { useCallback, useContext, useState } from 'react';

import styled from '@emotion/styled';
import { colors } from '../../global';
import { useFormFields, useStoreData } from '../../hooks';
import { ApiContext, CoreContext } from '../../providers';
import { media } from '../../services/media';
import { addUserData, setError } from '../../state';

import { ButtonsRemoveProfile } from './ButtonsRemoveProfile';
import { ProfileDataSource } from './ProfileDataSource';
import { ProfileFormContext } from './context';
import { ButtonToggleRemoveProfile } from './ButtonToggleRemoveProfile';
import { Button } from '../../components/Button';
import { Fields } from '../../components/Fields';
import { Loader } from '../../components/Loader';
import { Storyblok, StoryblokSection } from '../../storyblok';

/**
 * handleDeleteDatasource is temporary disabled so code relatd to that
 * functino have been commented out. Related code:
 * - UpScoreAPI import
 * - handleDeleteDatasource function
 * - ProfileDataSource prop onClick
 * - ProfileDataSource prop buttonTitle
 * - buttonRemoveSourceLabel from sectionData
 */
export const ProfileForm = ({
    sectionData,
}: StoryblokSection<Storyblok.Component.ProfileForm>) => {
    const { store } = useContext(CoreContext);
    const { Upscore } = useContext(ApiContext);
    const profile = useStoreData().user.profileData;
    /**
     * Toggled whenever the profile form is submitted.
     */
    const [loadingState, setLoadingState] = useState<boolean>(false);

    /**
     * Show returned message when profile form is submitted.
     */
    const [responseMessage, setResponseMessage] = useState<string>();

    /**
     * Show/hide additional delete confirmation profile step
     */
    const [removeProfileState, setRemoveProfileState] =
        useState<boolean>(false);

    const {
        heading,
        fields,
        buttonSaveProfileLabel,
        responseSuccess,
        responseError,
        datasourcesHeading,
        // buttonRemoveSourceLabel,
    } = sectionData;
    const [fieldValues, setFieldValues] = useFormFields(fields, profile);

    /**
     * Parse available data into `save profile` request body.
     *
     * NOTE: The address is commented out because currently there isn't
     * `address field` set in the CMS. If you want to use the
     * address data please create matching field in the Storyblok then remove
     * this note and comment.
     */
    const parseProfileToPostBody = useCallback(() => {
        const { name, email, phoneNumber } = fieldValues;

        return {
            name,
            email,
            phoneNumber,
            sources: profile.sources.map((source: any) => source.idSource),
            // address
        };
    }, [fieldValues, profile.sources]);

    /** @type {(event: React.FormEvent<HTMLFormElement>) => void} */
    const handleFormSubmit = useCallback(
        async (event) => {
            event.preventDefault();
            setLoadingState(true);

            try {
                const postBody = parseProfileToPostBody();

                const response = await Upscore.postUserProfile(postBody);

                if (response.result === true) {
                    await Upscore.getUserProfile().then((p) =>
                        store.dispatch(addUserData('profileData', p || null)),
                    );
                    setResponseMessage(responseSuccess);
                    setLoadingState(false);
                } else {
                    setResponseMessage(responseError);
                }
            } catch (error) {
                console.error(error);
                store.dispatch(setError('general'));
            }
        },
        [parseProfileToPostBody, responseError, responseSuccess],
    );

    // /** @type {(id: number) => void} */
    const handleDeleteDatasource = useCallback((id) => {
        // UpscoreAPI.delete(`/user/profile/source/${id}`)
        //     .then((response) => {
        //         if (response.result === true) {
        //             callForData('profile');
        //         }
        //     })
        //     .catch((error) => {
        //         console.error(error);
        //     });
    }, []);

    return (
        <ProfileFormContext.Provider
            value={{
                fieldValues,
                sectionData,
                loadingState,
                setLoadingState,
                profile,
                setResponseMessage,
                setRemoveProfileState,
            }}
        >
            <Section>
                <form onSubmit={handleFormSubmit}>
                    {heading && <h2>{heading}</h2>}
                    {loadingState && (
                        <LoaderWrapper>
                            <Loader backgroundColor="#54ef93" />
                        </LoaderWrapper>
                    )}
                    {responseMessage && (
                        <ResponseMessage>{responseMessage}</ResponseMessage>
                    )}
                    <FieldsWrapper>
                        <Fields
                            fields={fields}
                            fieldValues={fieldValues}
                            setFieldValues={setFieldValues}
                            inputType="inputField"
                        />
                    </FieldsWrapper>

                    {datasourcesHeading && <h2>{datasourcesHeading}</h2>}
                    <DatasourceWrapper>
                        {profile.sources &&
                            profile.sources.map(
                                ({ source, logo, idSource }: any) => (
                                    <ProfileDataSource
                                        key={source}
                                        image={logo}
                                        idSource={idSource}
                                        name={source}
                                        onClick={() =>
                                            handleDeleteDatasource(idSource)
                                        }
                                        // buttonTitle={buttonRemoveSourceLabel}
                                    />
                                ),
                            )}
                    </DatasourceWrapper>

                    <ButtonsWrapper>
                        {removeProfileState ? (
                            <ButtonsRemoveProfile />
                        ) : (
                            <>
                                <Button
                                    variant="primary"
                                    data-event="profile form (save profile button)"
                                    type="submit"
                                >
                                    {buttonSaveProfileLabel}
                                </Button>
                                <ButtonToggleRemoveProfile />
                            </>
                        )}
                    </ButtonsWrapper>
                </form>
            </Section>
        </ProfileFormContext.Provider>
    );
};

const Section = styled.section`
    padding: 80px 0;
`;

const FieldsWrapper = styled.div`
    display: grid;

    ${media.max('lg')} {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    ${media.min('lg')} {
        grid-template-columns: repeat(2, 1fr);
        gap: 40px;
    }
`;

const ResponseMessage = styled.div`
    width: 100%;
    margin-bottom: 25px;
    text-align: center;
    font-weight: 500;
    font-family: $font-family-2;
    font-size: 16px;

    ${media.min('lg')} {
        font-size: 18px;
    }
`;

const LoaderWrapper = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(${colors.bonJour}, 0.7);
    z-index: 2;
`;

const DatasourceWrapper = styled.div`
    display: flex;
    flex-flow: row wrap;
    justify-content: space-around;
`;

const ButtonsWrapper = styled.div`
    text-align: center;
    padding-top: 25px;
    border-top: 1px solid rgba(0, 0, 0, 0.05);
    flex-direction: column;
    justify-content: center;
    display: grid;
    gap: 20px;

    ${media.min('sm')} {
        grid-template-columns: 1fr;
    }

    ${media.min('md')} {
        grid-template-columns: 1fr 1fr;
        padding-top: 60px;
        max-width: 600px;
        margin: 0 auto;
    }
`;
